import React from 'react';
import { usePlayer } from '../PlayerProvider';
import { useAppSelector } from '@/store/store';
import { selectAllAudioFeatures } from '@/features/videos/videosSlice';
import { formatKeyShort } from '@meradio/music-utils';

interface TrackInfoProps {
  className?: string;
  size?: 'small' | 'medium' | 'large';
  showThumbnail?: boolean;
  showAudioFeatures?: boolean;
}

const TrackInfo: React.FC<TrackInfoProps> = ({
  className = '',
  size = 'medium',
  showThumbnail = true,
  showAudioFeatures = true,
}) => {
  const { currentVideo } = usePlayer();
  const audioFeatures = useAppSelector(selectAllAudioFeatures);

  if (!currentVideo) {
    return (
      <div className={`flex items-center space-x-3 ${className}`}>
        <div className="flex-1 text-center">
          <div className="text-white text-opacity-60 text-sm">
            No track selected
          </div>
        </div>
      </div>
    );
  }
  
  const parseTrackTitle = (title: string) => {
    const patterns = [
      /^(.+?)\s*[-–]\s*(.+?)(\s*\[(.+?)\])?\s*$/,
      /^(.+?)\s*:\s*(.+?)(\s*\[(.+?)\])?\s*$/,
      /^(.+?)\s*-\s*(.+?)(\s*\((.+?)\))?\s*$/,
    ];
    
    for (const pattern of patterns) {
      const match = title.match(pattern);
      if (match) {
        return {
          artist: match[1]?.trim() || 'Unknown Artist',
          track: match[2]?.trim() || title,
          album: match[4]?.trim() || null,
        };
      }
    }

    return {
      artist: 'Unknown Artist',
      track: title,
      album: null,
    };
  };
  
  const { artist, track, album } = parseTrackTitle(currentVideo.title);
  const videoAudioFeatures = audioFeatures[currentVideo.videoId];
  
  const sizeClasses = {
    small: {
      thumbnail: 'w-10 h-10',
      title: 'text-sm',
      subtitle: 'text-xs',
      features: 'space-x-1',
    },
    medium: {
      thumbnail: 'w-12 h-12',
      title: 'text-base',
      subtitle: 'text-sm',
      features: 'space-x-2',
    },
    large: {
      thumbnail: 'w-16 h-16',
      title: 'text-lg',
      subtitle: 'text-base',
      features: 'space-x-3',
    },
  };
  
  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {showThumbnail && currentVideo.thumbnailUrl && (
        <div className={`flex-shrink-0 ${sizeClasses[size].thumbnail}`}>
          <img
            src={currentVideo.thumbnailUrl}
            alt={`${track} thumbnail`}
            className="w-full h-full object-cover rounded-lg shadow-sm"
            loading="lazy"
          />
        </div>
      )}
      
      <div className="flex-1 min-w-0">
        <div className="flex flex-col space-y-1">
          <div
            className={`
              text-white font-semibold truncate
              ${sizeClasses[size].title}
            `}
            title={track}
          >
            {track}
          </div>

          <div
            className={`
              text-white text-opacity-70 truncate
              ${sizeClasses[size].subtitle}
            `}
            title={artist}
          >
            {artist}
          </div>

          {album && (
            <div
              className={`
                text-white text-opacity-50 truncate
                ${size === 'small' ? 'text-xs' : 'text-xs'}
              `}
              title={album}
            >
              from "{album}"
            </div>
          )}

          {showAudioFeatures && videoAudioFeatures && (
            <div className={`flex items-center ${sizeClasses[size].features} mt-1`}>
              <span className="text-xs text-meradio-dark-light dark:text-text-dark-secondary">
                {Math.round(videoAudioFeatures.tempo)} · {formatKeyShort(videoAudioFeatures.key, videoAudioFeatures.mode)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackInfo;
